class HeaderMenu {
    constructor(root) {
        this.root = root;
        this.topBar = root.querySelector('[data-header-top]');
        this.menuWrap = root.querySelector('[data-header-menu]');
        this.toggleBtn = root.querySelector('[data-header-toggle]');
        this.closeBtn = root.querySelector('[data-header-close]');
        this.overlay = root.querySelector('[data-header-overlay]');
        this.menuPanels = this.menuWrap?.querySelector('.sp-header__menu-panels');
        this.rootPanel = this.menuWrap?.querySelector('[data-menu-panel="root"]');
        this.mobileHeader = this.menuWrap?.querySelector(
            '[data-header-mobile-header]'
        );
        this.mobileHeaderLogo = this.menuWrap?.querySelector(
            '[data-header-mobile-logo]'
        );
        this.mobileHeaderBack = this.menuWrap?.querySelector(
            '[data-header-mobile-back]'
        );
        this.mobileHeaderTitle = this.menuWrap?.querySelector(
            '[data-header-mobile-title]'
        );
        this.mobileMedia = window.matchMedia('(max-width: 1023px)');
        this.panelRegistry = [];
        this.panelStack = [];
        this.lastScroll = window.scrollY;
        this.lastToggleTime = 0;
        this.ticking = false;
        this.lastHeaderHeight = null;
    }

    init() {
        if (!this.menuWrap || !this.rootPanel) return;
        this.setupPanels();
        this.bindEvents();
        this.resetPanels();
        this.applyPanelPlacement(this.mobileMedia.matches);
        this.setupMegaHover();
        this.handleScroll();
        this.updateHeaderHeight();
        window.addEventListener('resize', () => this.updateHeaderHeight());
    }

    setupPanels() {
        this.panels = [this.rootPanel];
        const itemsWithChildren = this.menuWrap.querySelectorAll(
            '.menu-item-has-children'
        );

        itemsWithChildren.forEach((item, index) => {
            const link =
                item.querySelector(':scope > .sp-header__menu-link') ||
                item.querySelector(':scope > a');
            const submenu = item.querySelector(':scope > .sp-header__submenu');

            if (!link || !submenu) return;

            submenu.classList.add('sp-header__menu-panel');
            submenu.dataset.panelId = `submenu-${index}`;
            const linkLabel = link.textContent.trim();
            if (linkLabel) {
                submenu.dataset.panelTitle = linkLabel;
            }
            this.panels.push(submenu);
            if (!this.panelRegistry.find(entry => entry.panel === submenu)) {
                this.panelRegistry.push({
                    panel: submenu,
                    parent: submenu.parentElement,
                    nextSibling: submenu.nextElementSibling,
                });
            }

            if (!link.querySelector('.sp-header__caret')) {
                const caret = document.createElement('i');
                caret.className =
                    'sp-header__caret icon icon-chevron-down ml-auto text-[20px] transition-transform lg:ml-0';
                link.appendChild(caret);
            }

            link.addEventListener('click', event => {
                if (!this.mobileMedia.matches) return;
                const submenuItems = Array.from(submenu.children).filter(
                    child => !child.classList.contains('sp-header__submenu-back')
                );
                if (!submenuItems.length) {
                    return;
                }
                event.preventDefault();
                this.openPanel(submenu);
            });
        });
    }

    bindEvents() {
        if (this.toggleBtn) {
            this.toggleBtn.addEventListener('click', () => {
                if (this.menuWrap.classList.contains('translate-x-full')) {
                    this.openMenu();
                } else {
                    this.closeMenu();
                }
            });
        }

        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', () => this.closeMenu());
        }

        if (this.mobileHeaderBack) {
            this.mobileHeaderBack.addEventListener('click', event => {
                event.preventDefault();
                this.closePanel();
            });
        }

        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.closeMenu());
        }

        if (this.mobileMedia.addEventListener) {
            this.mobileMedia.addEventListener('change', () => {
                this.resetPanels();
                this.applyPanelPlacement(this.mobileMedia.matches);
                this.closeMenu();
            });
        }
    }

    openMenu() {
        this.menuWrap.classList.remove('translate-x-full');
        this.menuWrap.classList.add('translate-x-0');
        if (this.overlay) {
            this.overlay.classList.remove('opacity-0', 'pointer-events-none');
            this.overlay.classList.add('opacity-100', 'pointer-events-auto');
        }
        document.body.classList.add('overflow-hidden');
        this.resetPanels();
    }

    closeMenu() {
        this.menuWrap.classList.add('translate-x-full');
        this.menuWrap.classList.remove('translate-x-0');
        if (this.overlay) {
            this.overlay.classList.add('opacity-0', 'pointer-events-none');
            this.overlay.classList.remove('opacity-100', 'pointer-events-auto');
        }
        document.body.classList.remove('overflow-hidden');
        this.resetPanels();
    }

    resetPanels() {
        this.panelStack = [this.rootPanel];
        this.panels.forEach(panel => {
            panel.classList.remove('is-active', 'is-prev');
        });
        this.rootPanel.classList.add('is-active');
        this.updateMobileHeader(this.rootPanel);
    }

    applyPanelPlacement(isMobile) {
        if (!this.menuPanels) return;
        this.panelRegistry.forEach(entry => {
            const { panel, parent, nextSibling } = entry;
            if (isMobile) {
                if (panel.parentElement !== this.menuPanels) {
                    this.menuPanels.appendChild(panel);
                }
                return;
            }

            if (panel.parentElement !== parent && parent) {
                if (nextSibling && nextSibling.parentElement === parent) {
                    parent.insertBefore(panel, nextSibling);
                } else {
                    parent.appendChild(panel);
                }
            }
        });
    }

    setupMegaHover() {
        const leftLists = this.menuWrap.querySelectorAll(
            '.sp-header__submenu--left-list'
        );
        leftLists.forEach(list => {
            const mega = list.closest('.sp-header__mega');
            const allItems = Array.from(
                list.querySelectorAll(':scope > .menu-item')
            );
            if (!allItems.length) return;

            const childItems = allItems.filter(item =>
                item.classList.contains('menu-item-has-children')
            );
            const panels = mega
                ? Array.from(mega.querySelectorAll('[data-mega-panel]'))
                : [];

            const clearActive = () => {
                childItems.forEach(item => item.classList.remove('is-active'));
                panels.forEach(panel => panel.classList.remove('is-active'));
            };

            const setActive = item => {
                childItems.forEach(current => {
                    current.classList.toggle('is-active', current === item);
                });
                const panelId = item?.dataset?.megaTarget;
                panels.forEach(panel => {
                    panel.classList.toggle(
                        'is-active',
                        panel.dataset.megaPanel === panelId
                    );
                });
            };

            clearActive();
            if (childItems.length) {
                setActive(childItems[0]);
            }

            allItems.forEach(item => {
                item.addEventListener('mouseenter', () => {
                    if (this.mobileMedia.matches) return;
                    if (item.classList.contains('menu-item-has-children')) {
                        setActive(item);
                    } else {
                        clearActive();
                    }
                });
                item.addEventListener('focusin', () => {
                    if (this.mobileMedia.matches) return;
                    if (item.classList.contains('menu-item-has-children')) {
                        setActive(item);
                    } else {
                        clearActive();
                    }
                });
            });
        });
    }

    openPanel(panel) {
        const current = this.panelStack[this.panelStack.length - 1];
        if (panel === current) return;

        current.classList.remove('is-active');
        current.classList.add('is-prev');

        panel.classList.add('is-active');
        panel.classList.remove('is-prev');
        this.panelStack.push(panel);
        this.updateMobileHeader(panel);
    }

    closePanel() {
        if (this.panelStack.length <= 1) return;
        const current = this.panelStack.pop();
        current.classList.remove('is-active');

        const previous = this.panelStack[this.panelStack.length - 1];
        previous.classList.add('is-active');
        previous.classList.remove('is-prev');
        this.updateMobileHeader(previous);
    }

    updateMobileHeader(panel) {
        if (!this.mobileHeader) return;
        const isRoot = panel === this.rootPanel;
        if (this.mobileHeaderLogo) {
            this.mobileHeaderLogo.classList.toggle('is-hidden', !isRoot);
        }
        if (this.mobileHeaderBack) {
            this.mobileHeaderBack.classList.toggle('is-hidden', isRoot);
        }
        if (this.mobileHeaderTitle) {
            if (isRoot) {
                this.mobileHeaderTitle.textContent = '';
                return;
            }
            const title = panel?.dataset?.panelTitle || '';
            this.mobileHeaderTitle.textContent = title;
        }
    }

    handleScroll() {
        const onScroll = () => {
            const current = window.scrollY;
            const delta = current - this.lastScroll;
            const hideThreshold = 140;
            const showThreshold = 50;
            const toggleCooldown = 220;
            let headerStateChanged = false;

            if (this.topBar && Math.abs(delta) > 6) {
                const isHidden = this.root.classList.contains('is-top-hidden');
                const shouldHide = delta > 0 && current > hideThreshold;
                const shouldShow = delta < 0 || current < showThreshold;
                const now = Date.now();

                if (!isHidden && shouldHide && now - this.lastToggleTime > toggleCooldown) {
                    this.root.classList.add('is-top-hidden');
                    this.lastToggleTime = now;
                    headerStateChanged = true;
                } else if (isHidden && shouldShow && now - this.lastToggleTime > toggleCooldown) {
                    this.root.classList.remove('is-top-hidden');
                    this.lastToggleTime = now;
                    headerStateChanged = true;
                }
            }

            this.lastScroll = current;

            if (headerStateChanged) {
                this.updateHeaderHeight();
            }
            this.ticking = false;
        };

        window.addEventListener('scroll', () => {
            if (!this.ticking) {
                window.requestAnimationFrame(onScroll);
                this.ticking = true;
            }
        });
    }

    updateHeaderHeight() {
        const height = this.root.getBoundingClientRect().height;
        if (height === this.lastHeaderHeight) {
            return;
        }
        this.lastHeaderHeight = height;
        document.documentElement.style.setProperty(
            '--header-height',
            `${height}px`
        );
    }
}

const header = document.querySelector('[data-header]');
if (header) {
    const headerMenu = new HeaderMenu(header);
    headerMenu.init();
}
